import React from 'react';
import {
  HeaderSkeleton,
  SearchBarAndFiltersSkeleton,
  DataTableSkeleton,
  PaginationSkeleton,
} from './';

interface StudentSkeletonProps {
  /**
   * Whether to show the skeleton with a semi-transparent background (for use during refreshes)
   */
  transparent?: boolean;

  /**
   * Additional class names to apply to the skeleton container
   */
  className?: string;

  /**
   * Number of filter options to show in the skeleton
   */
  filterCount?: number;

  /**
   * Number of columns to show in the data table skeleton
   */
  columnCount?: number;

  /**
   * Number of rows to show in the data table skeleton
   */
  rowCount?: number;

  /**
   * Whether to include bulk action controls in the skeleton
   */
  includeBulkActions?: boolean;
}

/**
 * StudentSkeleton component
 *
 * This component provides a loading skeleton specifically designed for student-related pages.
 * It includes header, search bar, filters, data table, and pagination skeletons.
 *
 * @example
 * // For initial loading
 * <StudentSkeleton />
 *
 * // For background refreshes
 * <StudentSkeleton transparent={true} />
 *
 * // With customized options
 * <StudentSkeleton
 *   filterCount={5}
 *   columnCount={8}
 *   rowCount={10}
 *   includeBulkActions={true}
 * />
 */
export const StudentSkeleton: React.FC<StudentSkeletonProps> = ({
  transparent = false,
  className = '',
  filterCount = 4,
  columnCount = 9,
  rowCount = 5,
  includeBulkActions = true,
}) => {
  const containerClasses = transparent
    ? `absolute inset-0 z-10 ${className}`
    : `min-h-screen bg-[#F5F8F9] ${className}`;

  const overlayClasses = transparent
    ? 'bg-white/80 backdrop-blur-[1px] rounded-lg'
    : '';

  return (
    <div className={containerClasses}>
      <div className={`${overlayClasses} h-full`}>
        <div className="max-w-[1600px] mx-auto p-6 space-y-8 pb-24 lg:pb-8">
          {/* Header skeleton */}
          <HeaderSkeleton buttonCount={2} />

          {/* Search and filters */}
          <div className="flex flex-wrap gap-6">
            <div className="flex-1">
              <SearchBarAndFiltersSkeleton filterCount={filterCount} />
            </div>
          </div>

          {/* Bulk actions skeleton (conditional) */}
          {includeBulkActions && (
            <div className="h-14 bg-gradient-to-r from-gray-100 to-gray-50 rounded-xl animate-pulse">
              <div className="flex items-center h-full px-4">
                <div className="w-24 h-6 bg-gray-200 rounded-md"></div>
                <div className="ml-4 flex space-x-2">
                  <div className="w-20 h-8 bg-gray-200 rounded-md"></div>
                  <div className="w-20 h-8 bg-gray-200 rounded-md"></div>
                </div>
              </div>
            </div>
          )}

          {/* Table skeleton */}
          <DataTableSkeleton
            rowCount={rowCount}
            columnCount={columnCount}
            withCheckbox={true}
            withActions={true}
          />

          {/* Pagination */}
          <PaginationSkeleton />
        </div>
      </div>
    </div>
  );
};
